/**
 * Status footer — single source of truth for connectivity and posture context.
 *
 * Replaces the legacy StatusBar. Phase 2 of the redesign — see
 * docs/plans/2026-04-28-frontend-redesign.md §5.1 and Phase 2 acceptance.
 *
 * Phase 2 ships the visual + a clickable socket status that opens a popover
 * with the last error. The full diagnostics popover ships in Phase 6 with
 * receipts.
 */

import { useEffect, useState } from 'react'
import { useTradingStore } from '../../state/trading'

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function StatusFooter() {
  const wsConnected = useTradingStore((s) => s.wsConnected)
  const tradingMode = useTradingStore((s) => s.tradingMode())
  const [now, setNow] = useState(() => new Date())
  const [showSocketPopover, setShowSocketPopover] = useState(false)

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const socketLabel = wsConnected ? 'Socket transport connected' : 'Socket transport disconnected'
  const socketColor = wsConnected ? 'bg-capital' : 'bg-risk'
  const socketTextColor = wsConnected ? 'text-muted' : 'text-risk'

  const versionLabel = tradingMode
    ? `Aegis · ${tradingMode === 'unknown' ? 'Mode unknown' : tradingMode.toUpperCase()}`
    : 'Aegis'

  return (
    <footer
      role="contentinfo"
      aria-label="System status"
      className="flex items-center justify-between border-t border-rule px-8 py-3 font-mono text-[11px] tracking-[0.05em] text-dim"
    >
      <span>{versionLabel}</span>

      <div className="relative">
        <button
          type="button"
          onClick={() => setShowSocketPopover((v) => !v)}
          className={`flex items-center gap-2 ${socketTextColor} hover:text-bone`}
          aria-haspopup="dialog"
          aria-expanded={showSocketPopover}
          data-testid="socket-status-button"
        >
          <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${socketColor}`} />
          {socketLabel}
        </button>

        {showSocketPopover && !wsConnected && (
          <div
            role="dialog"
            aria-label="Socket diagnostics"
            className="absolute bottom-full right-0 mb-2 w-72 border border-rule-2 bg-ink p-4"
          >
            <p className="font-serif text-[15px] leading-relaxed text-bone">
              The realtime data socket is offline. Decisions on this page may be stale.
            </p>
            <p className="mt-2 font-mono text-[11px] text-muted">
              Reconnect attempts run automatically with exponential backoff. Detailed diagnostics
              ship in Phase 6.
            </p>
          </div>
        )}
        {showSocketPopover && wsConnected && (
          <div
            role="dialog"
            aria-label="Socket diagnostics"
            className="absolute bottom-full right-0 mb-2 w-72 border border-rule-2 bg-ink p-4"
          >
            <p className="font-serif text-[15px] leading-relaxed text-bone">
              The websocket transport is connected. REST remains authoritative for system safety.
            </p>
          </div>
        )}
      </div>

      <span className="font-mono">{formatTime(now)} ET</span>
    </footer>
  )
}
